import * as React from 'react';
import { useMemo,useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAppDispatch } from 'src/hooks/redux';
import { closeModal, showModal } from 'src/store/reducers/modalSlice';
import { setNewPassword } from 'src/store/reducers/passwordRestorationSlice';
import { isPassword } from 'src/utils/validation';

import { PasswordInput } from '../../components';
import { SuccessModal } from '../../components/Modal/SuccessModal';

export const SetNewPasswordScreen = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate();

  const [isPasswordSetSuccess, setIsPasswordSetSuccess] = useState(false);
  const [password, setPassword] = useState<string>('');
  const [repeatPassword, setRepeatPassword] = useState<string>('');

  const isSubmitButtonActive = useMemo(() => {
    return isPassword(password) && password === repeatPassword
  }, [password, repeatPassword])

  const onPasswordInputValidate = (value: string) => {
    if (!isPassword(value)) {
      return 'Password must be at least 8 characters long.';
    }
    setPassword(value);
    return '';
  }

  const onRepeatPasswordInputValidate = (value: string) => {
    if (value !== password) {
      return 'Passwords do not match.';
    }
    setRepeatPassword(value);
    return '';
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const password = data.get('password');
    const repeatPassword = data.get('repeat-password');

    const path = new URLSearchParams(window.location.search);
    const token = path.get("token");

    if (!onPasswordInputValidate(password as string)
      && !onRepeatPasswordInputValidate!(repeatPassword as string)
    ) {
      dispatch(setNewPassword(
        {
          password,
          authToken: token,
          onSuccess: () => {
            setIsPasswordSetSuccess(true);
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        },
      ))
    }
  };

  return (
    <Box display="flex">
      {
        isPasswordSetSuccess
          ? (<SuccessModal
            successText="Password has been reset successfully, please log in"
            okButtonTitle="Ok, got it"
            onButtonClick={() => {
              navigate('/')
            }}/>)
          : (
            <Box sx={{
              borderRadius: 4,
              paddingX: 4,
              maxWidth: 494,
              backgroundColor: 'white',
            }}>
              <Box
                sx={{
                  marginTop: '40px',
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center',
                  // alignItems: 'center',
                }}
              >
                <Box sx={{
                  display: 'flex',
                  position: 'relative',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  flex: 1,
                }}>
                  <Box sx={{
                    mb: 4
                  }}>
                    <img
                      alt="Logo"
                      width="40px"
                      src="/images/logo.png"
                    />
                  </Box>
                </Box>
                <Box sx={{
                  mb: 3
                }}>
                  <Typography component="h1" variant="h2">
                    Reset password
                  </Typography>
                  <Typography sx={{
                    color: '#363636',
                    fontSize: '16px',
                    fontWeight: '400',
                    marginTop: '40px',
                    textAlign: 'left',
                  }}>
                    Please enter a new password below
                  </Typography>
                </Box>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <PasswordInput
                    label="New Password"
                    id="password-input"
                    name="password"
                    initialValue=""
                    validationFn={onPasswordInputValidate}
                  />
                  <PasswordInput
                    label="Re-enter new password"
                    id="repeat-password-input"
                    name="repeat-password"
                    initialValue=""
                    validationFn={onRepeatPasswordInputValidate}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    size="large"
                    disableRipple
                    disabled={!isSubmitButtonActive}
                    disableElevation
                    variant="contained"
                    sx={{mt: 3, mb: 4}}
                  >
                    Save new password
                  </Button>
                </Box>
              </Box>
            </Box>
          )
      }
    </Box>
  );
};
